import type { IDBPDatabase } from "idb";
import { openDB } from "idb";

import type { TierSchema } from "@/utils/subscriptions";

import type { DataFields } from "./editor-context";

const DB_NAME = "DraftsDB";
const STORE_NAME = "drafts";
const AUTO_SAVE_EXPIRY = 7 * 24 * 60 * 60 * 1000; // 1 week in milliseconds

export type Draft = {
  id: string; // Unique identifier for the draft
  publicKey: string; // User identifier
  dataFields: Partial<DataFields>;
  subscriptionTier: TierSchema | null;
  isAutoSave: boolean;
  updatedAt: number; // Timestamp of the last update
};

let dbPromise: Promise<IDBPDatabase> | null = null;

const getDB = async (): Promise<IDBPDatabase> => {
  if (!dbPromise) {
    dbPromise = openDB(DB_NAME, 1, {
      upgrade(db) {
        if (!db.objectStoreNames.contains(STORE_NAME)) {
          const store = db.createObjectStore(STORE_NAME, { keyPath: "id" });
          store.createIndex("publicKey", "publicKey");
          store.createIndex("isAutoSave", "isAutoSave");
          store.createIndex("updatedAt", "updatedAt");
        }
      },
    });
  }
  return dbPromise;
};

// Save a draft (manual or autosave)
export const saveDraft = async (
  publicKey: string,
  dataFields: DataFields,
  subscriptionTier: TierSchema | null,
  isAutoSave: boolean = false,
): Promise<void> => {
  const db = await getDB();
  const id = `${publicKey}-${isAutoSave ? "auto" : "manual"}-${Date.now()}`;
  const updatedAt = Date.now();

  // Clean up old autosaves
  if (isAutoSave) {
    const oneWeekAgo = Date.now() - AUTO_SAVE_EXPIRY;
    const oldDrafts = await db.getAllFromIndex(STORE_NAME, "updatedAt");
    for (const draft of oldDrafts) {
      if (draft.isAutoSave && draft.updatedAt < oneWeekAgo) {
        await db.delete(STORE_NAME, draft.id);
      }
    }
  }

  // We don't want to store the file itself, we replace it with a dummy. The files are already uploaded at this stage.
  dataFields.media = dataFields.media.map((f) => ({
    ...f,
    file: new File([], "media-item"),
  }));

  dataFields.gatedMedia = dataFields.gatedMedia.map((f) => ({
    ...f,
    file: new File([], "media-item"),
  }));

  await db.put(STORE_NAME, {
    id,
    publicKey,
    dataFields,
    subscriptionTier,
    isAutoSave,
    updatedAt,
  });
};

// Get drafts for a specific user
export const getDrafts = async (
  publicKey: string,
  isAutoSave?: boolean,
): Promise<Draft[]> => {
  const db = await getDB();
  const drafts = await db.getAllFromIndex(STORE_NAME, "publicKey", publicKey);

  if (isAutoSave !== undefined) {
    return drafts.filter((draft) => draft.isAutoSave === isAutoSave);
  }
  return drafts;
};

// Delete a specific draft
export const deleteDraft = async (id: string): Promise<void> => {
  const db = await getDB();
  await db.delete(STORE_NAME, id);
};

// Clear all drafts for a specific user
export const clearDrafts = async (publicKey: string): Promise<void> => {
  const db = await getDB();
  const drafts = await db.getAllFromIndex(STORE_NAME, "publicKey", publicKey);
  for (const draft of drafts) {
    await db.delete(STORE_NAME, draft.id);
  }
};
